import styled from 'styled-components';

export const PageWrapper = styled.div`
  padding: 0 20px 40px;

  @media (max-width: 480px) {
    padding: 0 12px 32px;
  }
`;

/* ── TOP HEADER BAR ── */
export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 14px 0;

  .page-title {
    font-size: 15px;
    font-weight: 800;
    color: #f1f5f9;
    display: flex;
    align-items: center;
    gap: 8px;
    svg { color: #9333EA; }
  }

  .reset-info {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #6b7280;

    .reset-timer {
      color: #a78bfa;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      direction: ltr;
    }
  }
`;

export const AdBanner = styled.div`
  width: calc(100% + 40px);
  margin: 0 -20px 16px;
  height: 72px;
  background: #111;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2a2a2a;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

/* ── SERVER LIST ── */
export const ServerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/* ── SINGLE SERVER CARD ── */
export const ServerCard = styled.div`
  display: block;
  text-decoration: none;
  background: #161616;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: rgba(255, 255, 255, 0.12);
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
  }

  /* ── CARD HEADER: rank + server name ── */
  .card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 14px;
    background: #1c1c1c;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);

    .rank-badge {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;

      .rank-medal { font-size: 16px; line-height: 1; }

      .rank-num {
        font-size: 13px;
        font-weight: 900;
        line-height: 1;
      }
    }

    .server-name {
      flex: 1;
      min-width: 0;
      font-size: 13.5px;
      font-weight: 700;
      color: #e2e8f0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: center;
    }
  }

  /* ── CARD BODY: info | image | vote ── */
  .card-body {
    display: flex;
    height: 300px;

    @media (max-width: 560px) {
      flex-direction: column;
      height: auto;
    }
  }

  /* ── INFO COL (rightmost in RTL) ── */
  .info-col {
    width: 165px;
    flex-shrink: 0;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    border-inline-end: 1px solid rgba(255, 255, 255, 0.04);

    @media (max-width: 560px) {
      width: 100%;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      border-inline-end: none;
      border-bottom: 1px solid rgba(255,255,255,0.04);
      padding: 10px 12px;
    }

    .srv-title {
      font-size: 13px;
      font-weight: 800;
      color: #e2e8f0;
      line-height: 1.3;
      word-break: break-word;
    }

    .badges {
      display: flex;
      flex-direction: column;
      gap: 5px;

      .verified-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        background: rgba(22, 163, 74, 0.12);
        color: #4ade80;
        border: 1px solid rgba(74, 222, 128, 0.22);
        font-size: 10.5px;
        font-weight: 700;
        padding: 3px 9px;
        border-radius: 5px;
        width: fit-content;
        svg { font-size: 10px; }
      }

      .type-badge {
        font-size: 10px;
        font-weight: 800;
        padding: 3px 9px;
        border-radius: 5px;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        width: fit-content;
      }
    }

    .stats {
      display: flex;
      flex-direction: column;
      gap: 5px;

      .stat {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        color: #6b7280;

        svg { font-size: 11px; flex-shrink: 0; color: #374151; }
        .s-label { color: #6b7280; }
        .s-val { color: #cbd5e1; font-weight: 600; }
      }
    }
  }

  /* ── IMAGE COL (center, flex fills remaining width) ── */
  .img-col {
    flex: 1;
    min-width: 0;
    position: relative;
    overflow: hidden;
    background: #0a0a14;

    img {
      width: 100%;
      height: 100%;
      object-fit: stretch;
      object-position: center;
      display: block;
      transition: transform 0.5s ease;
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background:
        linear-gradient(to right, rgba(22,22,22,0.65) 0%, transparent 30%),
        linear-gradient(to left,  rgba(22,22,22,0.65) 0%, transparent 30%);
      pointer-events: none;
    }

    @media (max-width: 560px) {
      min-height: 200px;
    }
  }

  &:hover .img-col img { transform: scale(1.05); }

  /* ── VOTE COL (leftmost in RTL) ── */
  .vote-col {
    width: 120px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-inline-start: 1px solid rgba(255, 255, 255, 0.04);

    @media (max-width: 560px) {
      width: 100%;
      height: 60px;
      border-inline-start: none;
      border-top: 1px solid rgba(255,255,255,0.04);
    }

    .vote-btn {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 96px;
      height: 48px;
      border-radius: 10px;
      border: none;
      background: #16a34a;
      color: #fff;
      cursor: pointer;
      font-family: inherit;
      transition: all 0.18s;

      svg { font-size: 18px; flex-shrink: 0; }

      .count {
        font-size: 17px;
        font-weight: 800;
        direction: ltr;
        line-height: 1;
      }

      &:hover {
        background: #15803d;
        transform: scale(1.05);
        box-shadow: 0 4px 18px rgba(22, 163, 74, 0.35);
      }

      &.voted {
        background: #7c3aed;
        box-shadow: 0 4px 18px rgba(124, 58, 237, 0.35);
        cursor: default;
        &:hover { transform: none; box-shadow: 0 4px 18px rgba(124, 58, 237, 0.35); }
      }

      &.locked {
        background: #1f2937;
        color: #6b7280;
        cursor: not-allowed;
        &:hover { background: #1f2937; transform: none; box-shadow: none; }
      }
    }
  }
`;

/* rank color helpers */
export const RANK_COLORS = {
  1: { num: '#ffd700', glow: 'rgba(255,215,0,0.3)',  medal: '🥇' },
  2: { num: '#c0c0c0', glow: 'rgba(192,192,192,0.2)', medal: '🥈' },
  3: { num: '#cd7f32', glow: 'rgba(205,127,50,0.25)', medal: '🥉' },
};

export const TYPE_STYLES = {
  oldschool:    { bg: 'rgba(232,192,90,0.14)', color: '#e8c05a', border: 'rgba(232,192,90,0.3)' },
  newschool:    { bg: 'rgba(147,51,234,0.14)', color: '#a78bfa', border: 'rgba(147,51,234,0.3)' },
  middleschool: { bg: 'rgba(59,130,246,0.14)', color: '#60a5fa', border: 'rgba(59,130,246,0.3)' },
  custom:       { bg: 'rgba(239,68,68,0.14)',  color: '#f87171', border: 'rgba(239,68,68,0.3)'  },
};
